import {
  AutomationOperation,
  CreateAutomationPayload,
  SalesforceActions,
} from 'module/api/integrations/salesforce/types';
import { PartialSalesforceAutomationLocalData } from 'module/api/integrations/salesforce/types';
import {
  IntegrationConsumer,
  IntegrationProvider,
  IntegrationSource,
} from 'module/api/integrations/types';
import { toApiFormat } from 'module/ui/components/molecules/field-filter/simple-search-filter/simple-search-filter';

/** Backend expects teamMembers as comma-separated string e.g. "dev@rocketlane.com,test@rocketlane.com" */
function toTeamMembersString(
  value: unknown,
): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') return value || undefined;
  if (Array.isArray(value)) {
    const parts = value
      .map((item) =>
        typeof item === 'object' && item != null && 'valueStr' in item
          ? (item as { valueStr: string }).valueStr
          : typeof item === 'string'
            ? item
            : null,
      )
      .filter((s): s is string => s != null && s !== '');
    return parts.length > 0 ? parts.join(',') : undefined;
  }
  return undefined;
}

function toInputValue(value: unknown): string | undefined {
  if (value == null) return undefined;

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'object') {
    const option = value as { valueStr?: unknown; value?: unknown };

    if (option.valueStr != null && option.valueStr !== '') {
      return String(option.valueStr);
    }

    if (option.value != null && option.value !== '') {
      return String(option.value);
    }
  }

  return undefined;
}

export const transformSfToApiFormat = (
  integrationId: number,
  automationDetails: PartialSalesforceAutomationLocalData,
): CreateAutomationPayload | undefined => {
  if (
    automationDetails.triggers?.type &&
    automationDetails.salesforceObject != null
  ) {
    return {
      automationName: automationDetails.automationName,
      description: automationDetails.description,
      integrationType: IntegrationProvider.SalesForce,
      object: automationDetails.salesforceObject.value as string,
      triggerCondition: {
        match: automationDetails.triggers.type,
        nativeFields: (automationDetails?.triggers?.criteria ?? []).map(
          (criterion) => ({
            name: criterion.field.fieldId.toString(),
            ...toApiFormat(criterion),
          }),
        ),
      },
      matchingCondition: automationDetails?.matches
        ? {
          match: automationDetails?.matches?.type,
          nativeFields: (automationDetails?.matches?.criteria ?? []).map(
            (match) => ({
              name: match.field.fieldId.toString(),
              ...toApiFormat(match),
            }),
          ),
        }
        : undefined,
      actions: [
        ...getActions(automationDetails),
        ...getContactActions(automationDetails),
      ],
      source: IntegrationSource.NativeIntegration,
      sourceId: integrationId,
      enabled: true,
    };
  }

  return undefined;
};

export const getAccountActions = (
  automationDetails: PartialSalesforceAutomationLocalData,
) => {
  return [
    {
      integrationType: IntegrationConsumer.Rocketlane,
      resourceType: SalesforceActions.Account,
      operation: AutomationOperation.Upsert,
      inputs: Object.keys(automationDetails?.accountFields ?? {}).reduce(
        (acc, eachField) =>
          eachField === 'teamMembers'
            ? {
              ...acc,
              [eachField]: toTeamMembersString(
                automationDetails?.accountFields?.[eachField],
              ),
            }
            : {
              ...acc,
              [eachField]: toInputValue(
                automationDetails?.accountFields?.[eachField],
              ),
            },
        {},
      ),
    },
  ];
};

const getActions = (
  automationDetails: PartialSalesforceAutomationLocalData,
) => {
  // Use company object (sourceParentResourceType) instead of trigger object
  const salesforceObject =
    automationDetails.sourceParentResourceType ||
    (automationDetails.salesforceObject?.value as string);

  if (automationDetails.salesForceType === SalesforceActions.Project) {
    const accountActions = getAccountActions(automationDetails);

    return [
      {
        integrationType: IntegrationConsumer.Rocketlane,
        resourceType: SalesforceActions.Project,
        operation: AutomationOperation.Upsert,
        inputs: Object.keys(automationDetails?.rocketlaneFields ?? {}).reduce(
          (acc, eachField) =>
            eachField === 'teamMembers'
              ? {
                ...acc,
                [eachField]: toTeamMembersString(
                  automationDetails?.rocketlaneFields?.[eachField],
                ),
              }
              : {
                ...acc,
                [eachField]: toInputValue(
                  automationDetails?.rocketlaneFields?.[eachField],
                ),
              },
          {},
        ),
        dependentActions: accountActions.map((action) => ({
          ...action,
          sourceResourceType: salesforceObject,
        })),
      },
    ];
  }

  if (automationDetails.salesForceType === SalesforceActions.Account) {
    return getAccountActions(automationDetails);
  }

  return [];
};

export const getContactActions = (
  automationDetails: PartialSalesforceAutomationLocalData,
) => {
  const {
    sourceParentResourceId,
    sourceParentResourceType,
    sourceResourceType,
    sourceRelationshipName,
    contactFields,
  } = automationDetails || {};

  const sourceParentResourceFieldId = (contactFields as any)?.contactObject
    ?.valueMeta?.sourceParentResourceFieldId;

  // Only check if sourceResourceType exists
  // This is the single source of truth for whether contact is enabled
  // If it exists, we must send the contact action to preserve configuration
  if (!sourceResourceType) {
    return [];
  }

  // Build inputs from contactFields (may be empty, that's OK)
  // Backend will use its sync configuration for field mappings
  const contactFieldInputs = Object.keys(contactFields ?? {}).reduce(
    (acc, eachField) => {
      if (eachField === 'contactObject') {
        return acc;
      }

      const fieldValue = contactFields?.[eachField];

      return {
        ...acc,
        [eachField]: toInputValue(fieldValue),
      };
    },
    {},
  );

  // Send contact action with metadata
  // Even if inputs is empty, backend needs this to maintain contact config
  return [
    {
      integrationType: IntegrationConsumer.Rocketlane,
      resourceType: SalesforceActions.Contact,
      operation: AutomationOperation.UpsertRelatedRecords,
      inputs: contactFieldInputs,
      sourceParentResourceType,
      generateAutomationForFutureInserts:
        automationDetails.generateAutomationForFutureInserts ?? false,
      sourceParentResourceId,
      sourceParentResourceFieldId,
      sourceResourceType,
      sourceRelationshipName,
    },
  ];
};
